/**
 * @file ExportModeFooter.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-10-02
 * @description The "What to do with it" step of an export, in the modal footer:
 *              the step label, the Download / Print / Email cards, and the
 *              modal's own action buttons beneath them.
 *
 *              The choice of what to do with the document sits beside the button
 *              that does it, not at the bottom of a long options column that may
 *              be scrolled out of view. Blend, formulation, table and document
 *              exports all use it, so the step looks and behaves the same in each.
 *
 *              Returns a fragment so the label, the cards and the button row are
 *              all direct flex items of .modal-footer. The footer wraps them onto
 *              three rows (Formulations.css, "Export modal").
 *
 *              The modal passes its two button <div>s as children, unchanged.
 *              useSlideOutButton is called here because this component mounts
 *              with those buttons. See CLAUDE.md on modal slide-outs.
 */

import React from 'react';
import { useSlideOutButton } from './SlideOutButton';
import './Formulations.css';

/** The three modes, worded as the blend and formulation exports word them.
 *  A modal whose wording differs passes its own list in the same shape. */
export const EXPORT_MODES = [
  { key: 'download', icon: 'download', label: 'Download', hint: 'Save the PDF to this device' },
  { key: 'print', icon: 'print', label: 'Print', hint: 'Send straight to the office printer' },
  { key: 'email', icon: 'mail', label: 'Email', hint: 'Attach it to an email' }
];

/** What can take focus inside a revealed block: the first of these in the
 *  block's own order is where its keyboard order starts. A Dropdown's trigger
 *  is a <button>, hence buttons too ("Send from" in the blend export). */
const FIELD = [
  'input:not([type="hidden"]):not(:disabled)', 'textarea:not(:disabled)',
  'select:not(:disabled)', 'button:not(:disabled)', '[tabindex]:not([tabindex="-1"])'
].join(', ');

/** Whether a node is wholly inside the nearest ancestor that scrolls it (the
 *  modal body), or inside the window if nothing does. */
function inView(node) {
  let box = node.parentElement;
  while (box && !/(auto|scroll)/.test(window.getComputedStyle(box).overflowY)) {
    box = box.parentElement;
  }
  const r = node.getBoundingClientRect();
  const b = box ? box.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
  return r.top >= b.top && r.bottom <= b.bottom;
}

/**
 * Ref callback for the fields a mode reveals in the body, such as the email
 * To / Subject / Message block. The mode is picked in the footer, and those
 * fields appear at the bottom of the options column, which may be scrolled out
 * of view, and EARLIER in the DOM than the card that revealed them: the modal
 * renders its body before its footer. Tab from the card went to Cancel, and the
 * block was reachable only by Shift+Tab, last field first.
 *
 * So focus moves to the block's first field. The view moves only when that
 * field is out of sight, and then it brings in the whole block ('nearest': the
 * least scroll that shows it, top first if it is taller than the view). When
 * the first field can already be seen, the view stays put: the document
 * export's pager sits above its options in the same scroll, and scrolling to
 * show the Message box as well pushed it out of view.
 *
 * Defined at module level so its identity never changes. React calls it when
 * the block mounts, so on picking Email, and not on every keystroke inside it.
 */
export const revealOnMount = (el) => {
  if (!el) return;
  const field = el.querySelector(FIELD);
  if (!inView(field || el) && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
  if (field) field.focus({ preventScroll: true });
};

/**
 * @param {Array}    modes     [{ key, icon, label, hint, disabled?, title? }]
 * @param {string}   mode      The selected key
 * @param {Function} onMode    Called with the key of the card clicked
 * @param {boolean}  busy      An export is running (re-measures the buttons)
 * @param {string}   stepLabel The step heading above the cards
 * @param {React.Node} children The footer's button <div>s
 */
export default function ExportModeFooter({
  modes = EXPORT_MODES, mode, onMode, busy = false,
  stepLabel = 'What to do with it', children
}) {
  useSlideOutButton([mode, busy]);

  return (
    <>
      <div className="fm-export-step">{stepLabel}</div>
      <div className="fm-export-modes" role="group" aria-label={stepLabel}>
        {modes.map((m) => (
          // data-read-safe: a footer button is greyed for a view-only account
          // by default (App.css), and choosing a mode writes nothing. The
          // action button and the server still decide what the account may
          // do, and a mode it may not use is passed in as disabled.
          <button key={m.key} type="button" data-read-safe
                  aria-pressed={mode === m.key}
                  className={`fm-export-mode${mode === m.key ? ' active' : ''}`}
                  disabled={!!m.disabled}
                  title={m.title}
                  onClick={() => onMode(m.key)}>
            <span className="material-symbols-rounded" aria-hidden="true">{m.icon}</span>
            <span className="fm-export-mode-label">{m.label}</span>
            <span className="fm-export-mode-hint">{m.hint}</span>
          </button>
        ))}
      </div>
      <div className="buttons slideout">{children}</div>
    </>
  );
}
